import awsRumActionNames from '../../consts/aws/awsRumActionNames';
import awsRumActionTypes from '../../consts/aws/awsRumActionTypes';
import awsRumStatusTypes from '../../consts/aws/awsRumStatusTypes';
import {strCode} from '../../consts/strCode';
import {selectIotDeviceByCodentify} from '../../state/selectors/iotDevices';
import {
    selectRetailDeviceBySerialNumber,
    selectRetailDeviceByUserId,
    selectRetailDevices,
    selectUserIds,
} from '../../state/selectors/retail';
import {selectConsentsFlagsValues} from '../../state/selectors/session';
import {setDeviceAuthToken} from '../../state/slice/iotDevices';
import {
    setRetailBadges,
    setRetailDeviceAuthToken,
    setUserRewardsIsLoading,
    updateRetailDeviceData,
} from '../../state/slice/retail';
import {dispatch, getState} from '../../state/store';
import rumCustomEvents from '../events/rumCustomEvents';
import log from '../logger/log';
import notificationService from '../notificationService';
import uamClientService from '../uam/uamClientService';

const getRetailAuthToken = async (codentify) => {
    if (!codentify) return;

    try {
        const consents = selectConsentsFlagsValues(getState());

        const authToken = await uamClientService.postRetailAuthToken(codentify, consents);

        const device = selectIotDeviceByCodentify(codentify)(getState());

        if (device) {
            dispatch(setDeviceAuthToken(device?.serialNumber, authToken));
            dispatch(setRetailDeviceAuthToken(codentify, authToken));
        } else {
            dispatch(setRetailDeviceAuthToken(codentify, authToken));
        }

        log.info(`RETAIL: Successfully get retail device auth token ${codentify}`);

        return authToken;
    } catch (e) {
        log.error(`RETAIL: Failed to get retail device auth token ${codentify}`, e);

        throw e;
    }
};

const addRetailDevice = async (codentify) => {
    if (!codentify) return;

    try {
        const response = await uamClientService.getRetailDevices(false, codentify);

        log.info(`RETAIL: Successfully retail device added ${codentify}`);

        return response;
    } catch (e) {
        log.error(`RETAIL: Failed to add retail device ${codentify}`, e);
    }
};

const getUsersRewards = async () => {
    const userIds = selectUserIds(getState());

    if (!userIds?.length) return;
    try {
        const users_rewards = await uamClientService.getRetailRewards({user_ids: userIds});

        users_rewards.forEach(({summary, user_id}) => {
            const {serialNumber} = selectRetailDeviceByUserId(user_id)(getState()) || {};

            if (serialNumber) dispatch(updateRetailDeviceData(serialNumber, {rewards_summary: summary}));
        });
    } catch (e) {
        log.error(`RETAIL service: Failed to get users rewards`, e);
        const users_rewards = await uamClientService.getRetailRewards({user_ids: userIds, isCached: true});

        users_rewards.forEach(({summary, user_id}) => {
            const {serialNumber} = selectRetailDeviceByUserId(user_id)(getState()) || {};

            if (serialNumber) dispatch(updateRetailDeviceData(serialNumber, {rewards_summary: summary}));
        });
    }
};

// fetch badges for unique user_id and fan-out to all devices of this user
const fetchBadgesForUser = async (userId, devices) => {
    try {
        const badgeData = await uamClientService.getUserBadges({
            isThrowError: true,
            user_id: userId,
        });

        if (badgeData) {
            devices
                .filter((d) => d.userId === userId)
                .forEach((d) => dispatch(setRetailBadges(d.serialNumber, badgeData)));
        }
    } catch (e) {
        log.error(`RETAIL: Error for userId=${userId}`, e);
    }
};

const getUserRewards = async (serialNumber) => {
    const {userId} = selectRetailDeviceBySerialNumber(serialNumber)(getState()) || {};

    if (!userId) return;
    dispatch(setUserRewardsIsLoading(serialNumber, true));
    try {
        const rewards = await uamClientService.getRetailRewards({user_id: userId});

        dispatch(updateRetailDeviceData(serialNumber, {rewards}));
    } catch (e) {
        log.error(`RETAIL service: Failed to get user rewards ${userId}`, e);
        const rewards = await uamClientService.getRetailRewards({user_id: userId, isCached: true});

        dispatch(updateRetailDeviceData(serialNumber, {rewards}));
    } finally {
        dispatch(setUserRewardsIsLoading(serialNumber, false));
    }
};

const sendNotifyForUser = async (serialNumber) => {
    const {userId} = selectRetailDeviceBySerialNumber(serialNumber)(getState());

    if (!userId) {
        log.error(`RETAIL service: No userId for serialNumber=${serialNumber}`, null, serialNumber);
        return;
    }

    try {
        await uamClientService.postRetailUserVisit(userId);

        log.info(`RETAIL service: Successfully sent user notify for user ${userId}`, serialNumber);

        notificationService.showToastNotification({messageStr: strCode.DASHBOARD_PAGE_SUCCESSFULLY_SEND_NOTIF});

        rumCustomEvents.recordAction({
            action: awsRumActionTypes.STORE,
            rumActionName: awsRumActionNames.VISITED,
            status: awsRumStatusTypes.SUCCESS,
            userId,
        });
    } catch (e) {
        log.error(`RETAIL service: Failed to send user notify for user ${userId}`, e, serialNumber);

        rumCustomEvents.recordAction({
            action: awsRumActionTypes.STORE,
            rumActionName: awsRumActionNames.VISITED,
            status: awsRumStatusTypes.FAILED,
            userId,
        });

        notificationService.showToastNotification({
            messageStr: strCode.DASHBOARD_PAGE_ERROR_SEND_NOTIF,
        });
    }
};

const getUsersBadges = async () => {
    const devices = Object.values(selectRetailDevices(getState()) || {});

    if (!devices.length) return;

    try {
        log.info('RETAIL: Start getting badge data');

        const seen = new Set();

        await Promise.all(
            devices.map(async ({userId}) => {
                if (!userId || seen.has(userId)) return;
                seen.add(userId);

                await fetchBadgesForUser(userId, devices);
            })
        );
    } catch (e) {
        log.error('RETAIL: Unexpected error during getting badge data', e);
    }
};

export default {
    getRetailAuthToken,
    addRetailDevice,
    getUserRewards,
    getUsersRewards,
    getUsersBadges,
    sendNotifyForUser,
};
